// Copyright 2023, Durable
import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Chip,
  ClickAwayListener,
  Collapse
} from "@mui/material";

import {
  AuthContextType,
  ProfileContextType,
  fetchUsersFromProfiles,
  useAuth,
  useProfiles
} from "../components/shared/Auth";
import {
  Body_update_user_pic as bodyUpdateUserPic,
  UserPermission,
  ViewUser
} from "@durable/durable-client";
import { IconForUser } from "../components/shared/UserIcon";
import { Person as PersonIcon } from "@mui/icons-material";
import { onClickwithKeyboard } from "../helpers";
import { updateAvatar } from "../pages/SettingsPanels/DurableSettings";

/**
 * Convert a file to the desired type for upload.
 */
export function fileToBlob(file: File) {
  let blob: Blob = null;
  blob = file;
  return { file: blob };
}

/**
 * The dropdown handling all parts of a user's profile.
 */
function UserSelect({
  profileContext,
  authContext,
  selecting,
  setSelecting,
  user
}: {
  profileContext: ProfileContextType;
  authContext: AuthContextType;
  selecting: boolean;
  setSelecting: (arg: boolean) => void;
  user: ViewUser;
}) {
  const uploadFile = useRef<HTMLInputElement | null>(null);
  const [selectedAvatar, setSelectedAvatar] =
    useState<bodyUpdateUserPic>(null);

  // If a user has special permissions, display a chip
  let permissionTag = null;
  if (user.permissions.includes(UserPermission.DEBUG_TOOLS)) {
    permissionTag = (
      <Chip label="dev" size="small" color="secondary" className="ml-1" />
    );
  } else if (user.permissions.includes(UserPermission.SUPPORT_DASHBOARD)) {
    permissionTag = (
      <Chip label="support" size="small" color="primary" className="ml-1" />
    );
  }

  // Any time there's a change about the selected avatar, sync that with the
  // server.
  useEffect(() => {
    if (selectedAvatar) {
      updateAvatar(selectedAvatar);
    }
  }, [selectedAvatar]);

  /**
   * Open the file picker. The file picker is spawned from a hidden input
   * element.
   */
  function beginUpload() {
    uploadFile.current.click();
  }

  return (
    <div className="pointer-events-none absolute right-0 top-0">
      <Collapse in={selecting} unmountOnExit>
        <ClickAwayListener onClickAway={() => setSelecting(false)}>
          <Card className="pointer-events-auto mt-12 p-3 shadow-lg">
            <div className="flex w-full flex-col items-center justify-center">
              <Avatar
                className={`relative mb-2 h-16 w-16 cursor-pointer
            bg-durableTealDark-300 text-coolGrey-1000`}
                src={user.profile_picture_url}
                {...onClickwithKeyboard(beginUpload)}
              >
                <div
                  className={`absolute flex h-full w-full items-center 
                justify-center bg-coolGrey-100/90 text-center 
              text-xs font-semibold text-coolGrey-1000 opacity-0 
               transition-opacity hover:opacity-80`}
                >
                  Upload Avatar
                </div>
                {user.profile_picture_url ?? (
                  <PersonIcon className="text-5xl" />
                )}
              </Avatar>
              <div className="mb-3 flex items-center text-lg">
                {user.username}
                {permissionTag}
              </div>
              <input
                type="file"
                id="fileUpload"
                onChange={(event) => {
                  setSelectedAvatar(fileToBlob(event.target.files[0]));
                }}
                ref={uploadFile}
                className="hidden h-0 w-0 opacity-0"
                accept="image/png"
              />
            </div>
            <div className="flex w-full min-w-[13rem] justify-center">
              <Button
                variant="text"
                onClick={() => profileContext.setCurrentProfile(null)}
              >
                Switch User
              </Button>
              <Button
                variant="text"
                onClick={() => authContext.handleLogout()}
              >
                Log Out
              </Button>
            </div>
          </Card>
        </ClickAwayListener>
      </Collapse>
    </div>
  );
}

/**
 * Handles login and registration, as well as displays the current user in the
 * top right of the screen.
 *
 * @return {React.ReactElement}
 */
function UserPortal() {
  const [selecting, setSelecting] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [otherUsers, setOtherUsers] = useState<ViewUser[]>([]);
  const authContext = useAuth();
  const { user } = authContext;

  const profileContext = useProfiles();

  useEffect(() => {
    if (user !== undefined && user !== null) {
      fetchUsersFromProfiles(
        Array.from(profileContext.profiles.values()),
        setOtherUsers,
        profileContext.removeProfile,
        user.id
      );
    }
  }, [profileContext.profiles, profileContext.removeProfile, user]);

  return (
    <>
      <div id="user-management-box">
        {!user ? (
          <></>
        ) : (
          <div className="relative ml-1">
            <IconForUser
              user={user}
              onClick={() => setSelecting(!selecting)}
            />
            <UserSelect
              user={user}
              profileContext={profileContext}
              authContext={authContext}
              selecting={selecting}
              setSelecting={setSelecting}
            />
          </div>
        )}
      </div>
    </>
  );
}

export { UserPortal };
